// Use if we want to enable chunks in the future
// eslint-disable-next-line no-undef
// __webpack_public_path__ = window.assetsPath;

import deepmerge from "deepmerge";
import { h, Fragment, render } from 'preact';

import { Snap } from "@searchspring/snap-preact";
import { getContext, DomTargeter } from "@searchspring/snap-toolbox";
import { tabManager } from "./middleware/plugins/tabManager";

import { afterStore } from "./middleware/plugins/afterStore";
import { configurable } from "./middleware/plugins/configurable";
import { combineMerge } from "./middleware/functions";

/* context from script tag */
const context = getContext(['collection', 'tags', 'template', 'shopper', 'format', 'blog']);

/* create settings config for re-usable variables */
const settings = {
	products: {
		id: 'products',
		label: 'Recommended Products',
		siteId: 'kf8822',
		icon: '',
	},
	articles: {
		id: 'articles',
		label: 'Articles & Content',
		siteId: 'vkdptx',
		icon: '',
	},
	search: {
		page: window.location.href.includes('/search') ? true : false,
	},
	ac: {
		input: '.ss__search-desktop',
		target: 'ss__autocomplete--target',
		after: '.search-modal__form',
    input_mobile:'.ss__search-mobile',
    after_mobile: '.search-modal__form-mobile'
	},
};

/* set up site details config */
let site = {
	id: context?.siteId ? context.siteId : settings.products.siteId,
	currency: 'usd',
	lang: 'en',
	parameters: {
		query: 'q',
		page: 'page',
	},
	features: {
		integratedSpellCorrection: {
			enabled: true,
		},
	},
};

/* set up page details config */
let page = {
	id: settings.search.page ? 'search' : 'other',
	title: settings.search.page ? 'Search Results' : 'Other Page',
	type: settings.search.page ? 'search' : 'other',
};

/* background filters */
let backgroundFilters = [];
let backgroundFiltersArticles = [];

if (context.collection?.handle) {
	// update page details when on collection
	page = {
		id: context.collection.handle,
		title: context.collection.name,
		type: 'collection',
	};
	// set background filter
	if (context.collection.handle != 'all') {
    backgroundFilters.push({
      field: 'collection_handle',
      value: context.collection.handle,
      type: 'value',
      background: true,
    });
  }

	// handle collection tags (filters)
	if (context.tags) {
		const collectionTags = context.tags
			.toLowerCase()
			.replace(/\&quot\;/g, '"')
			.replace(/-/g, '')
			.replace(/ +/g, '')
			.split('|');
		collectionTags.forEach((tag) => {
			backgroundFilters.push({
				field: 'ss_tags',
				value: tag,
				type: 'value',
				background: true,
			});
		});
	}
}

if (context.blog?.handle && context.blog.handle !== 'learning-center' && context.blog.handle !== 'magazine') {
  backgroundFiltersArticles.push({
		field: 'type',
		value: context.blog.handle,
		type: 'value',
		background: true,
	});
}

/* configuration and instantiation */
let config = {
	context,
	url: {
		parameters: {
			core: {
				query: { name: site.parameters.query },
				page: { name: site.parameters.page },
			},
		},
	},
	client: {
		globals: {
			siteId: site.id,
		},
	},
	features: site.features,
	instantiators: {
		recommendation: {
			components: {
				Default: async () => {
          return (await import("./components/Recommendations/Recs/Recs")).Recs;
        },
			},
			config: {
				branch: 'develop',
			},
		},
	},
	controllers: {
		search: [
			{
				url: {
					parameters: {
						core: {
							sort: { name: 'psort' },
							filter: { name: 'pfilter' },
							page: { name: 'ppage' },
						},
					},
				},
				config: {
					id: settings.products.id,
					plugins: [[afterStore], [configurable]],
					isSearch: settings.search.page,
					globals: {
						filters: backgroundFilters,
						redirectResponse: 'direct',
						pagination: {
							pageSize: window?.ssConfig?.pageSize ?? 24,
						},
					},
					settings: {
						redirects: {
							singleResult: false,
						},
            infinite: { 
              backfill: 5, 
            }
					},
				},
			},
			{
				url: {
					parameters: {
						core: {
							sort: { name: 'asort' },
							filter: { name: 'afilter' },
							page: { name: 'apage' },
						},
					},
				},
				config: {
					id: settings.articles.id,
					plugins: [[afterStore], [configurable]],
					isSearch: settings.search.page,
					globals: {
            			filters: backgroundFiltersArticles,
						siteId: settings.articles.siteId,
					},
					settings: {
						redirects: {
							singleResult: false,
						},
            infinite: { 
              backfill: 5, 
            }
					},
				},
			},
		],
		autocomplete: [
			{
				config: {
					id: 'ac-products',
					selector: settings.ac.input,
					plugins: [[afterStore], [configurable]],
					settings: {
						history: {
							limit: 6,
							showResults: true,
						},
						trending: {
							limit: 6,
							showResults: true,
						},
					},
					globals: {
						facets: {
							limit: 4,
							valueLimit: 10,
						},
						pagination: {
							pageSize: 6,
						},
						merchandising: {
							disableInlineBanners: true,
							redirect: ''
						},
						redirectResponse: 'direct'
					},
				},
			},
			{
				config: {
					id: 'ac-articles',
					selector: settings.ac.input,
					plugins: [[afterStore], [configurable]],
					settings: {
						history: {
							limit: 6,
							showResults: true,
						},
						trending: {
							limit: 6,
							showResults: true,
						},
					},
					globals: {
						siteId: settings.articles.siteId,
						facets: {
							limit: 4,
							valueLimit: 10,
						},
						pagination: {
							pageSize: 6,
						},
					},
				},
			},
      {
				config: {
					id: 'ac-products-mobile',
					selector: settings.ac.input_mobile,
					plugins: [[afterStore], [configurable]],
					settings: {
						history: {
							limit: 6,
							showResults: true,
						},
						trending: {
							limit: 6,
							showResults: true,
						},
					},
					globals: {
						facets: {
							limit: 4,
							valueLimit: 10,
						},
						pagination: {
							pageSize: 6,
						},
						merchandising: {
							disableInlineBanners: true,
						},
						redirectResponse: 'direct'
					},
				},
			},
			{
				config: {
					id: 'ac-articles-mobile',
					selector: settings.ac.input_mobile,
					plugins: [[afterStore], [configurable]],
					settings: {
						history: {
							limit: 6,
							showResults: true,
						},
						trending: {
							limit: 6,
							showResults: true,
						},
					},
					globals: {
						siteId: settings.articles.siteId,
						facets: {
							limit: 4,
							valueLimit: 10,
						},
						pagination: {
							pageSize: 6,
						},
					},
				},
			},
		],
	},
};

// used to add config settings from cypress e2e tests
if (window?.mergeSnapConfig) {
  config = deepmerge(config, window.mergeSnapConfig, {
    arrayMerge: combineMerge,
  });
}

const snap = new Snap(config);

snap.getControllers('products', 'articles').then(([products, articles, noResults]) => {
	/* create search tab manager */
	const tabs = new tabManager({
		getDefault: (tabs) =>  tabs.find(tab => tab.controller.store.pagination.totalResults)?.id,
		param: 'view',
		tabs: [
			{
				id: settings.products.id,
				label: settings.products.label,
				controller: products,
				icon: settings.products.icon,
			},
			{
				id: settings.articles.id,
				label: settings.articles.label,
				controller: articles,
				icon: settings.articles.icon,
			},
		],
	});

	new DomTargeter(
		[
			{
				selector: '#searchspring-content',
				component: async () => {
					return (await import('./components/Content/Content')).Content;
				},
				hideTarget: true,
			},
		],
		async (target, element) => {
			const additionalProps = target?.props ? target.props : {};
			const Component = await target.component();
			render(<Component tabs={tabs} {...additionalProps} />, element);
		}
	);

	/* recommendations for no results */
	products.noResultsController = noResults;
	articles.noResultsController = noResults;
});

snap.getControllers('ac-products', 'ac-articles').then(([acProducts, acArticles]) => {
	/* create autocomplete tab manager */
	const acTabs = new tabManager({
		getDefault: (tabs) =>  tabs.find(tab => tab.controller.store.pagination.totalResults)?.id,
		param: 'view',
		tabs: [
			{
				id: settings.products.id,
				label: settings.products.label,
				controller: acProducts,
				icon: settings.products.icon,
			},
			{
				id: settings.articles.id,
				label: settings.articles.label,
				controller: acArticles,
				icon: settings.articles.icon,
			},
		],
	});

	new DomTargeter(
		[
			{
				name: 'main',
				selector: settings.ac.after,
				inject: {
					action: 'after', // before, after, append, prepend
					element: () => {
						const acContainer = document.createElement('div');
						acContainer.className = settings.ac.target;
						acContainer.addEventListener('click', (e) => {
							e.stopPropagation();
						});
						return acContainer;
					},
				},
				props: {
					input: settings.ac.input,
				},
			},
		],

		async (target, element, originalElement) => {
			const inputElement = document.querySelector(settings.ac.input);

			const Autocomplete = (await import('./components/Autocomplete/Autocomplete')).Autocomplete;
			render(<Autocomplete input={inputElement} tabs={acTabs} />, element);
			acProducts.unbind();
			acArticles.unbind();
			acTabs.active.controller.bind();
		},
	);
});

snap.getControllers('ac-products-mobile', 'ac-articles-mobile').then(([acProducts, acArticles]) => {
	/* create autocomplete tab manager */
	const acTabs = new tabManager({
		getDefault: (tabs) =>  tabs.find(tab => tab.controller.store.pagination.totalResults)?.id,
		param: 'view',
		tabs: [
			{
				id: settings.products.id,
				label: settings.products.label,
				controller: acProducts,
				icon: settings.products.icon,
			},
			{
				id: settings.articles.id,
				label: settings.articles.label,
				controller: acArticles,
				icon: settings.articles.icon,
			},
		],
	});

	new DomTargeter(
		[
      {
				name: 'main-mobile',
				selector: settings.ac.after_mobile,
				inject: {
					action: 'after', // before, after, append, prepend
					element: () => {
						const acContainer = document.createElement('div');
						acContainer.className = settings.ac.target;
						acContainer.addEventListener('click', (e) => {
							e.stopPropagation();
						});
						return acContainer;
					},
				},
				props: {
					input: settings.ac.input_mobile,
				},
			},
		],

		async (target, element, originalElement) => {
			const inputElement = document.querySelector(settings.ac.input_mobile);
			const Autocomplete = (await import('./components/Autocomplete/Autocomplete')).Autocomplete;
			render(<Autocomplete input={inputElement} tabs={acTabs} />, element);
			acProducts.unbind();
			acArticles.unbind();
			acTabs.active.controller.bind();
		},
	);
});
